// API service for Hat Trading Platform
// Use environment variable for API base URL to support both local and production
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Helper for API calls
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };
  
  // Add auth token if available
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  console.log('API: Making request to:', url);
  console.log('API: Request config:', config);
  
  try {
    const response = await fetch(url, config);
    console.log('API: Response status:', response.status);
    console.log('API: Response headers:', response.headers);
    
    // Debug: Log the raw response text
    const responseText = await response.text();
    console.log('API: Raw response text:', responseText);
    
    if (!response.ok) {
      let errorObj = {};
      try { errorObj = responseText ? JSON.parse(responseText) : {}; } catch (_) { errorObj = {}; }
      const message = (errorObj && (errorObj.error || errorObj.message)) || responseText || `HTTP ${response.status}`;
      console.error('API: Response not OK:', { status: response.status, body: responseText });
      throw new Error(message || 'API error');
    }
    
    // Parse JSON only if we have content
    let data = {};
    if (responseText.trim()) {
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('API: Failed to parse JSON:', e);
        throw new Error('Invalid JSON response from server');
      }
    }
    console.log('API: Response data:', data);
    return data;
  } catch (error) {
    console.error('API: Request failed:', error);
    // Check if it's a network error (backend not running)
    if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
      throw new Error('Network error contacting the server. Please ensure the backend is running.');
    }
    throw error;
  }
};

// Auth API
export const authAPI = {
  login: async ({ email, password }) => {
    try {
      console.log('API: Attempting login for:', email);
      const res = await apiCall('/api/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      console.log('API: Login response received:', res);
      if (res.token) {
        localStorage.setItem('authToken', res.token);
        localStorage.setItem('currentUser', JSON.stringify(res.user));
        console.log('API: Token and user stored in localStorage');
      }
      return res;
    } catch (error) {
      console.error('API: Login failed:', error);
      throw error;
    }
  },
  register: async (user) => {
    try {
      const res = await apiCall('/api/register', {
        method: 'POST',
        body: JSON.stringify(user),
      });
      if (res.token) {
        localStorage.setItem('authToken', res.token);
        localStorage.setItem('currentUser', JSON.stringify(res.user));
      }
      return res;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  },
  logout: () => {
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  },
  getCurrentUser: () => {
    try {
      const user = localStorage.getItem('currentUser');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },
  resendVerificationEmail: async ({ email }) => {
    try {
      const res = await apiCall('/api/verify-email/resend', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      return res;
    } catch (error) {
      console.error('Resend verification email failed:', error);
      throw error;
    }
  },
  verifyEmail: async (token) => {
    try {
      const res = await apiCall('/api/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      return res;
    } catch (error) {
      console.error('Email verification failed:', error);
      throw error;
    }
  },
};

// Hats API
export const hatsAPI = {
  getAll: async (params = {}) => {
    try {
      const { sort, order, page, perPage, owner } = params || {};
      const qs = new URLSearchParams();
      if (sort) qs.set('sort', sort);
      if (order) qs.set('order', order);
      if (page) qs.set('page', String(page));
      if (perPage) qs.set('per_page', String(perPage));
      if (owner) qs.set('owner', owner);
      const query = qs.toString();
      return await apiCall(`/api/hats${query ? `?${query}` : ''}`);
    } catch (error) {
      console.error('Failed to get hats:', error);
      return { items: [], page: 1, per_page: 0, total: 0 };
    }
  },
  getFeatured: async () => {
    try {
      return await apiCall('/api/hats/featured');
    } catch (error) {
      console.error('Failed to get featured hats:', error);
      return [];
    }
  },
  get: async (id) => apiCall(`/api/hats/${id}`),
  create: async (hat) => apiCall('/api/hats', {
    method: 'POST',
    body: JSON.stringify(hat),
  }),
  update: async (id, hat) => apiCall(`/api/hats/${id}`, {
    method: 'PUT',
    body: JSON.stringify(hat),
  }),
  delete: async (id) => apiCall(`/api/hats/${id}`, { method: 'DELETE' }),
  getMyHats: async () => apiCall('/api/hats/my-hats'),
  
  // Get admin messages for current user
  getAdminMessages: async () => apiCall('/api/hats/admin-messages'),
  
  // Mark admin message as read
  markAdminMessageRead: async (messageId) => apiCall(`/api/hats/admin-messages/${messageId}/mark-read`, {
    method: 'PUT'
  }),
  
  // User-to-admin support messaging
  sendUserMessage: async (message) => apiCall('/api/hats/user-messages', {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),
  getUserMessages: async () => apiCall('/api/hats/user-messages'),
  markAdminRepliesRead: async () => apiCall('/api/hats/user-messages/mark-admin-replies-read', {
    method: 'PUT',
  }),
};

// Profile API
export const profileAPI = {
  get: async () => apiCall('/api/user/profile'),
  update: async (profile) => apiCall('/api/user/profile', {
    method: 'PUT',
    body: JSON.stringify(profile),
  }),
  deleteAccount: async () => apiCall('/api/user/account', { 
    method: 'DELETE'
  }),
  requestPasswordReset: async (email) => apiCall('/api/password-reset/request', {
    method: 'POST',
    body: JSON.stringify({ email })
  }),
  confirmPasswordReset: async (token, new_password) => apiCall('/api/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify({ token, new_password })
  }),
  changePassword: async ({ currentPassword, newPassword }) => apiCall('/api/change-password', {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword }),
  }),
  generate2FASecret: async () => apiCall('/api/2fa/generate-secret', { method: 'POST' }),
  enable2FA: async ({ secret, verificationCode }) => apiCall('/api/2fa/enable', {
    method: 'POST',
    body: JSON.stringify({ secret, verificationCode }),
  }),
  disable2FA: async ({ password }) => apiCall('/api/2fa/disable', {
    method: 'POST',
    body: JSON.stringify({ password }),
  }),
};

// Trades API
export const tradesAPI = {
  create: async (tradeData) => apiCall('/api/trades', {
    method: 'POST',
    body: JSON.stringify(tradeData),
  }),
  getAll: async () => apiCall('/api/trades'),
  get: async (id) => apiCall(`/api/trades/${id}`),
  getMyTrades: async () => apiCall('/api/trades/my-trades'),
  getPendingTrades: async () => apiCall('/api/trades/pending'),
  getTradeHistory: async () => apiCall('/api/trades/history'),
  accept: async (id) => apiCall(`/api/trades/${id}/accept`, {
    method: 'PUT',
  }),
  reject: async (id) => apiCall(`/api/trades/${id}/reject`, {
    method: 'PUT',
  }),
  cancel: async (id) => apiCall(`/api/trades/${id}/cancel`, {
    method: 'PUT',
  }),
  modify: async (id, updates) => apiCall(`/api/trades/${id}/modify`, {
    method: 'PUT',
    body: JSON.stringify(updates || {}),
  }),
  update: async (id, tradeData) => apiCall(`/api/trades/${id}`, {
    method: 'PUT',
    body: JSON.stringify(tradeData),
  }),
  delete: async (id) => apiCall(`/api/trades/${id}`, { method: 'DELETE' }),
  pickupEligible: async (hatId) => apiCall('/api/trades/pickup-eligible', {
    method: 'POST',
    body: JSON.stringify({ hat_id: hatId }),
  }),
  shippingEstimate: async (hatId) => apiCall('/api/trades/shipping-estimate', {
    method: 'POST',
    body: JSON.stringify({ hat_id: hatId }),
  }),
  // Shipping:
  getOutboundLabel: async (tradeId) => apiCall(`/api/trades/${tradeId}/label`),
  getReturnLabel: async (tradeId) => apiCall(`/api/trades/${tradeId}/return-label`, { method: 'GET' }),
  regenerateLabels: async (tradeId) => apiCall(`/api/trades/${tradeId}/regenerate-labels`, { method: 'POST' }),
  // Trade acknowledgments:
  acknowledgeReceived: async (tradeId) => apiCall(`/api/trades/${tradeId}/ack/received`, { method: 'PUT' }),
  acknowledgeReturned: async (tradeId) => apiCall(`/api/trades/${tradeId}/ack/returned`, { method: 'PUT' }),
  sendReminder: async (tradeId) => apiCall(`/api/trades/${tradeId}/send-reminder`, { method: 'POST' }),
  // Trade messaging
  sendMessage: async (tradeId, message) => apiCall(`/api/trades/${tradeId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),
  getMessages: async (tradeId) => apiCall(`/api/trades/${tradeId}/messages`),
};

// User Balance API
export const balanceAPI = {
  getBalance: async () => apiCall('/api/user/balance'),
  addFunds: async (amount) => apiCall('/api/user/balance/add', {
    method: 'POST',
    body: JSON.stringify({ amount }),
  }),
  withdrawFunds: async (amount, note, method) => apiCall('/api/user/balance/withdraw', {
    method: 'POST',
    body: JSON.stringify({ amount, note, method }),
  }),
  getTransactionHistory: async () => apiCall('/api/user/balance/transactions'),
  getTransactions: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/api/user/balance/transactions?${queryString}` : '/api/user/balance/transactions';
    return apiCall(url);
  },
};

// Disputes API
export const disputesAPI = {
  create: async (disputeData) => apiCall('/api/disputes', {
    method: 'POST',
    body: JSON.stringify(disputeData),
  }),
  getAll: async () => apiCall('/api/disputes'),
  get: async (id) => apiCall(`/api/disputes/${id}`),
  getMyDisputes: async () => apiCall('/api/disputes/my-disputes'),
  update: async (id, disputeData) => apiCall(`/api/disputes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(disputeData),
  }),
  close: async (id) => apiCall(`/api/disputes/${id}/close`, {
    method: 'PUT',
  }),
  getDisputeMessages: async (disputeId) => apiCall(`/api/disputes/${disputeId}/messages`),
  sendDisputeMessage: async (disputeId, message) => apiCall(`/api/disputes/${disputeId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),
};

// Reviews API
export const reviewsAPI = {
  create: async (reviewData) => apiCall('/api/reviews', {
    method: 'POST',
    body: JSON.stringify(reviewData),
  }),
  getAll: async () => apiCall('/api/reviews'),
  get: async (id) => apiCall(`/api/reviews/${id}`),
  getUserReviews: async (userId) => apiCall(`/api/reviews/user/${userId}`),
  getMyReviews: async () => apiCall('/api/reviews/my-reviews'),
  getReceivedReviews: async () => apiCall('/api/reviews/received'),
  update: async (id, reviewData) => apiCall(`/api/reviews/${id}`, {
    method: 'PUT',
    body: JSON.stringify(reviewData),
  }),
  delete: async (id) => apiCall(`/api/reviews/${id}`, { method: 'DELETE' }),
  canReviewTrade: async (tradeId) => apiCall(`/api/trades/${tradeId}/can-review`),
};

// Admin API
export const adminAPI = {
  getAllUsers: async () => apiCall('/api/admin/users'),
  getUserDetails: async (userId) => apiCall(`/api/admin/users/${userId}/details`),
  getAllItems: async () => apiCall('/api/admin/items'),
  getAllTrades: async () => apiCall('/api/admin/trades'),
  getAllTradesPaged: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/api/admin/trades${qs ? `?${qs}` : ''}`);
  },
  getUserItems: async (userId) => apiCall(`/api/admin/users/${userId}/items`),
  updateUserStatus: async (userId, status) => apiCall(`/api/admin/users/${userId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }),
  deleteUser: async (userId) => apiCall(`/api/admin/users/${userId}`, { method: 'DELETE' }),
  sendMessage: async (userId, message) => apiCall(`/api/admin/users/${userId}/message`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),
  // User-to-admin messages (support)
  getAllUserMessages: async () => apiCall('/api/admin/user-messages'),
  getUserMessageThread: async (userId) => apiCall(`/api/admin/user-messages/${userId}`),
  replyToUserMessage: async (userId, message) => apiCall(`/api/admin/user-messages/${userId}/reply`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),
  markUserMessageRead: async (userId) => apiCall(`/api/admin/user-messages/${userId}/mark-read`, {
    method: 'PUT',
  }),
  updateItemStatus: async (itemId, status) => apiCall(`/api/admin/items/${itemId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }),
  updateFeaturedRank: async (itemId, rank) => apiCall(`/api/admin/items/${itemId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status: 'feature_rank', rank })
  }),
  featureItem: async (itemId, rank) => apiCall(`/api/admin/items/${itemId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status: 'feature_rank', rank })
  }),
  unfeatureItem: async (itemId) => apiCall(`/api/admin/items/${itemId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status: 'unfeature' })
  }),
  deleteItem: async (itemId) => apiCall(`/api/admin/items/${itemId}`, { method: 'DELETE' }),
  getSettings: async () => apiCall('/api/admin/settings'),
  // Transactions
  getTransactions: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/api/admin/transactions${qs ? `?${qs}` : ''}`);
  },
  exportTransactionsCSV: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/api/admin/transactions/export${qs ? `?${qs}` : ''}`;
    const headers = { 'Accept': 'text/csv' };
    const token = localStorage.getItem('authToken');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error('Failed to export CSV');
    const blob = await res.blob();
    return blob;
  },
  updateTradeFee: async (fee) => apiCall('/api/admin/settings/trade-fee', {
    method: 'PUT',
    body: JSON.stringify({ fee })
  }),
  updatePickupFee: async (fee) => apiCall('/api/admin/settings/pickup-fee', {
    method: 'PUT',
    body: JSON.stringify({ fee })
  }),
  updateFirstTradeFreePromo: async ({ enabled, start_date, end_date }) => apiCall('/api/admin/settings/first-trade-free', {
    method: 'PUT',
    body: JSON.stringify({ enabled, start_date, end_date })
  }),
  refundUser: async ({ userId, amount, reason, tradeId }) => apiCall('/api/admin/refund', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, amount, reason, trade_id: tradeId || null })
  }),
  adjustBalance: async ({ userId, amount, direction, reason, tradeId }) => apiCall('/api/admin/adjust-balance', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, amount, direction, reason, trade_id: tradeId || null })
  }),
  // Withdrawals
  getWithdrawals: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/api/admin/withdrawals${qs ? `?${qs}` : ''}`);
  },
  markWithdrawalPaid: async (withdrawalId) => apiCall(`/api/admin/withdrawals/${withdrawalId}/mark-paid`, { method: 'POST' }),
  markWithdrawalFailed: async (withdrawalId, admin_note) => apiCall(`/api/admin/withdrawals/${withdrawalId}/mark-failed`, {
    method: 'POST',
    body: JSON.stringify({ admin_note }),
  }),
  // Disputes
  getAllDisputes: async () => apiCall('/api/admin/disputes'),
  resolveDispute: async (disputeId, resolution) => apiCall(`/api/admin/disputes/${disputeId}/resolve`, {
    method: 'PUT',
    body: JSON.stringify({ resolution }),
  }),
  closeDispute: async (disputeId, reason) => apiCall(`/api/admin/disputes/${disputeId}/close`, {
    method: 'PUT',
    body: JSON.stringify({ reason }),
  }),
  getDisputeMessages: async (disputeId) => apiCall(`/disputes/${disputeId}/messages`),
  sendDisputeMessage: async (disputeId, message) => apiCall(`/disputes/${disputeId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),
  cleanupDuplicateDisputes: async () => apiCall('/api/admin/cleanup-duplicate-disputes', {
    method: 'POST',
  }),
  adminOverrideReceived: async (tradeId, userId) => apiCall(`/api/admin/trades/${tradeId}/override-received`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  }),
  adminOverrideReturned: async (tradeId, userId) => apiCall(`/api/admin/trades/${tradeId}/override-returned`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  }),
  // Testing: Fast forward trade to end date
  fastForwardTrade: async (tradeId) => apiCall(`/api/admin/fast-forward-trade/${tradeId}`, {
    method: 'POST',
  }),
  // Admin: Delete trade
  deleteTrade: async (tradeId) => apiCall(`/api/admin/trades/${tradeId}/delete`, {
    method: 'DELETE',
  }),
  // Admin: Delete transaction
  deleteTransaction: async (transactionId) => apiCall(`/api/admin/transactions/${transactionId}/delete`, {
    method: 'DELETE',
  }),
  // User balance and trades for admin perspective
  getUserBalance: async (userId) => apiCall(`/api/admin/users/${userId}/balance`),
  getUserPendingTrades: async (userId) => apiCall(`/api/admin/users/${userId}/pending-trades`),
  getTradeDetails: async (tradeId) => apiCall(`/api/trades/${tradeId}`),
  // Trade management
  flagTrade: async (tradeId, reason) => apiCall(`/api/admin/trades/${tradeId}/flag`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || 'Admin flagged trade' }),
  }),
  forceCompleteTrade: async (tradeId, action, reason, userId = null) => apiCall(`/api/admin/trades/${tradeId}/force-complete`, {
    method: 'POST',
    body: JSON.stringify({ 
      action: action || 'complete', 
      reason: reason || 'Admin override - trade force completed',
      user_id: userId 
    }),
  }),
  
  // Deleted accounts management
  getDeletedAccounts: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/api/admin/deleted-accounts${qs ? `?${qs}` : ''}`);
  },
  getDeletedAccountDetails: async (userId) => apiCall(`/api/admin/deleted-accounts/${userId}`),
  searchDeletedTransactions: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/api/admin/deleted-transactions${qs ? `?${qs}` : ''}`);
  },
  verifyDeletedAccountBalance: async (userId) => apiCall(`/api/admin/deleted-accounts/${userId}/balance`),
  processDeletedAccountRefund: async (userId, amount, reason) => apiCall(`/api/admin/deleted-accounts/${userId}/refund`, {
    method: 'POST',
    body: JSON.stringify({ amount, reason }),
  }),
  
  // Manual Deposits
  createManualDeposit: async (data) => apiCall('/api/deposits/manual', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getManualPaymentInfo: async () => apiCall('/api/deposits/manual/payment-info'),
  getUserManualDeposits: async () => apiCall('/api/user/manual-deposits'),
  getUserWithdrawals: async () => apiCall('/api/user/withdrawals'),
  updateManualDepositStatus: async (depositId, data) => apiCall(`/api/admin/manual-deposits/${depositId}/update-status`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getManualDeposits: async (status = 'pending') => apiCall(`/api/admin/manual-deposits?status=${status}`),
  processManualDeposit: async (depositId) => apiCall(`/api/admin/manual-deposits/${depositId}/process`, {
    method: 'POST',
  }),
  rejectManualDeposit: async (depositId) => apiCall(`/api/admin/manual-deposits/${depositId}/reject`, {
    method: 'POST',
  }),
  verifyUserEmail: async (email) => apiCall('/api/admin/verify-user-email', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
};

// Users API
export const usersAPI = {
  getCloset: async (userId, { page = 1, perPage = 12, sort = 'created_at', order = 'desc' } = {}) => {
    const params = new URLSearchParams({
      page: String(page),
      per_page: String(perPage),
      sort,
      order,
    });
    return apiCall(`/api/users/${userId}/closet?${params.toString()}`);
  },
  getProfile: async (userId) => apiCall(`/api/users/${userId}/closet?page=1&per_page=1`),
};

// Settings API (public)
export const settingsAPI = {
  // Public endpoint for site-wide settings (no auth required)
  get: async () => apiCall('/api/settings'),
};

// Payments API (Stripe)
export const paymentsAPI = {
  getConfig: async () => apiCall('/api/payments/config'),
  createIntent: async (amount) => apiCall('/api/payments/create-intent', {
    method: 'POST',
    body: JSON.stringify({ amount }),
  }),
  createCheckoutSession: async (amount, returnUrl) => apiCall('/api/payments/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify({ amount, return_url: returnUrl }),
  }),
  verifySession: async (session_id) => apiCall('/api/payments/verify-session', {
    method: 'POST',
    body: JSON.stringify({ session_id }),
  }),
};

// Shipping API (tracking and rates)
export const shippingAPI = {
  track: async ({ carrier, tracking }) => apiCall(`/api/shipping/track?carrier=${encodeURIComponent(carrier)}&tracking=${encodeURIComponent(tracking)}`),
  calculateRates: async (fromZip, toZip) => apiCall('/api/shipping/calculate-rates', {
    method: 'POST',
    body: JSON.stringify({ from_zip: fromZip, to_zip: toZip })
  }),
  getTradeTracking: async (tradeId) => apiCall(`/api/trades/${tradeId}/tracking`),
};

// Address validation API
export const addressAPI = {
  validate: async (addressData) => apiCall('/api/address/validate', { 
    method: 'POST', 
    body: JSON.stringify(addressData) 
  }),
};
